import { useEffect, useMemo, useState } from "react";
import { apiConstants, postGeneric } from "../../../common/constants";
import { useForm } from "react-hook-form";
import { FormProvider, RHFTextField } from '../../../common/hook-form';
import { Box, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import { LoadingButton } from "@mui/lab";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ReplayIcon from '@mui/icons-material/Replay';
import UndoIcon from '@mui/icons-material/Undo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FormField from "./formField";
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, SUBTLE, BORDER, PRIMARY_BLUE, SUCCESS, ERROR, WHITE, withOpacity } from '../../../styles/foundation/colors';
import { primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';

// Plain-language nouns for the live preview (kept in sync with factList values).
const FACT_NOUN = {
    ofdCount: 'failed attempts',
    ndrReason: 'NDR reason',
    paymentMode: 'payment mode',
    courierParentName: 'courier',
    daysSinceFirstAttempt: 'days since first attempt',
};

const secSx = {
    position: 'relative', border: `1px solid ${BORDER}`, borderRadius: '14px',
    p: 2, pt: 2.25, mb: 2, bgcolor: WHITE,
};
const secTagSx = (accent) => ({
    position: 'absolute', top: -11, left: 16, px: 1.25, py: '3px', borderRadius: '20px',
    bgcolor: accent, color: WHITE, fontFamily: FONT, fontSize: 10.5, fontWeight: WEIGHT.extra,
    letterSpacing: '0.08em', textTransform: 'uppercase',
});
const secLeadSx = { fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY, mb: 1.5 };

const choiceSx = (selected, accent) => ({
    flex: '1 1 220px', minWidth: 200, p: 1.75, borderRadius: '12px', cursor: 'pointer', bgcolor: WHITE,
    border: `1.5px solid ${selected ? accent : BORDER}`,
    boxShadow: selected ? `0 0 0 3px ${withOpacity(accent, 0.14)}` : 'none',
    transition: 'all .15s ease',
    '&:hover': { borderColor: selected ? accent : '#cbd5e1' },
});

const NDRCreateAutomatedRule = ({ setOpen, setReload, rules = [] }) => {
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : '';

    // Auto-generate the next rule name (rule_N) — users no longer type "rule_x".
    const nextRuleName = useMemo(() => {
        const nums = (rules || [])
            .map((r) => parseInt(String(r?.name || '').replace('rule_', ''), 10))
            .filter((n) => !Number.isNaN(n));
        return `rule_${(nums.length ? Math.max(...nums) : 0) + 1}`;
    }, [rules]);

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                ruleName: Yup.string().required().matches(/^rule_\d+$/),
                initialAction: Yup.string().required("Please choose an action"),
                daysToReattempt: Yup.mixed().when('initialAction', {
                    is: (v) => v === "reattempt",
                    then: Yup.number().typeError('Enter number of days').positive('Enter number of days').required("Enter number of days"),
                    otherwise: Yup.mixed().notRequired(),
                }),
            })
        ),
        defaultValues: {
            ruleName: nextRuleName,
            initialAction: "",
            daysToReattempt: "",
            list: [{ fact: "", fieldValue: [] }],
        },
        shouldUnregister: true,
    });

    const { handleSubmit, reset, control, watch, register, resetField, getValues, setValue, formState } = methods;

    useEffect(() => { setValue('ruleName', nextRuleName); }, [nextRuleName, setValue]);

    const onSubmitCreateRule = async (data) => {
        setLoading(true);
        try {
            const payload = { ruleList: data.ruleName, action: data.initialAction };
            if (data.daysToReattempt) payload.preferredDeliveryDate = data.daysToReattempt;
            data.list.forEach((item) => {
                if (!item.fact) return;
                payload[item.fact] = item.fact === "daysSinceFirstAttempt"
                    ? item.firstAttempt
                    : item.fieldValue?.map((value) => value.toString());
            });
            const res = await postGeneric(`${apiConstants.NDR_RULEUPDATE}?merchantOid=${merchantId}&status=ACTIVE`, payload);
            if (res.status === 200) {
                if (res.data?.status === 'ERROR') {
                    enqueueSnackbar(res.data.errorMsg || "Error creating NDR rule", { variant: "error" });
                } else {
                    enqueueSnackbar("Rule created successfully", { variant: "success" });
                    setOpen(false);
                    setReload((r) => r + 1);
                }
            }
        } catch (err) {
            enqueueSnackbar(err?.response?.data?.message || err.message || "Error creating rule", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    const action = watch('initialAction');
    const chooseAction = (val) => setValue('initialAction', val, { shouldValidate: true });

    // ── Live plain-English preview ──
    const watchedList = watch('list') || [];
    const days = watch('daysToReattempt');
    const joinOr = (arr) => (arr.length <= 1 ? String(arr[0] ?? '') : `${arr.slice(0, -1).join(', ')} or ${arr[arr.length - 1]}`);
    const conditionPhrase = watchedList
        .filter((c) => c?.fact)
        .map((c) => {
            const noun = FACT_NOUN[c.fact] || c.fact;
            if (c.fact === 'daysSinceFirstAttempt') return c.firstAttempt ? `${noun} is ${c.firstAttempt} or more` : noun;
            const vals = c.fieldValue || [];
            return vals.length ? `${noun} is ${joinOr(vals)}` : noun;
        });
    const conditionText = conditionPhrase.length ? conditionPhrase.join(' and ') : 'a delivery fails';
    const actionText = action === 'reattempt'
        ? `reattempt the delivery${days ? ` after ${days} day(s)` : ''}`
        : action === 'rto' ? 'return the shipment to origin (RTO)' : null;

    return (
        <Box sx={{ width: '100%' }}>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitCreateRule)}>
                {/* Hidden auto rule name */}
                <input type="hidden" {...register('ruleName')} />
                <Typography sx={{ fontFamily: FONT, fontSize: 11, color: SUBTLE, mb: 1.5 }}>
                    Rule ID: <b style={{ color: NAVY }}>{nextRuleName}</b> · assigned automatically
                </Typography>

                {/* IF */}
                <Box sx={secSx}>
                    <Box sx={secTagSx(PRIMARY_BLUE)}>If</Box>
                    <Typography sx={secLeadSx}>
                        When a delivery fails <Box component="span" sx={{ color: SUBTLE, fontWeight: WEIGHT.medium }}>and <b>all</b> of these are true:</Box>
                    </Typography>
                    <FormField control={control} register={register} watch={watch} reset={resetField} getValues={getValues} />
                </Box>

                {/* THEN */}
                <Box sx={secSx}>
                    <Box sx={secTagSx(SUCCESS)}>Then</Box>
                    <Typography sx={secLeadSx}>Automatically…</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Box sx={choiceSx(action === 'reattempt', SUCCESS)} onClick={() => chooseAction('reattempt')}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: withOpacity(SUCCESS, 0.12), color: SUCCESS }}>
                                    <ReplayIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>Reattempt delivery</Typography>
                            </Box>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.75, lineHeight: 1.45 }}>
                                Ask the courier to try delivering again.
                            </Typography>
                            {action === 'reattempt' && (
                                <Box sx={{ mt: 1.25 }} onClick={(e) => e.stopPropagation()}>
                                    <RHFTextField variant="filled" name="daysToReattempt" label="Reattempt after (days)" width="100%" size="small" type="number" />
                                </Box>
                            )}
                        </Box>
                        <Box sx={choiceSx(action === 'rto', ERROR)} onClick={() => chooseAction('rto')}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: withOpacity(ERROR, 0.12), color: ERROR }}>
                                    <UndoIcon sx={{ fontSize: 18 }} />
                                </Box>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>Return to origin (RTO)</Typography>
                            </Box>
                            <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.75, lineHeight: 1.45 }}>
                                Send the shipment back — don’t attempt again.
                            </Typography>
                        </Box>
                    </Box>
                    {formState.errors?.initialAction && (
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: ERROR, mt: 1 }}>
                            {formState.errors.initialAction.message}
                        </Typography>
                    )}
                </Box>

                {/* Live preview */}
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', bgcolor: NAVY, borderRadius: '12px', p: 1.75, mb: 2.5 }}>
                    <VisibilityOutlinedIcon sx={{ fontSize: 17, color: withOpacity(WHITE, 0.7), mt: '1px' }} />
                    <Box>
                        <Typography sx={{ fontFamily: FONT, fontSize: 10, fontWeight: WEIGHT.bold, letterSpacing: '0.1em', textTransform: 'uppercase', color: withOpacity(WHITE, 0.6), mb: 0.4 }}>
                            In plain English
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: WHITE, lineHeight: 1.6, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {actionText ? (
                                <>This rule will automatically <b style={{ color: '#5fe3d1' }}>{actionText}</b> for any shipment where <b style={{ color: '#7bb8ff' }}>{conditionText}</b>.</>
                            ) : (
                                <>This rule applies to any shipment where <b style={{ color: '#7bb8ff' }}>{conditionText}</b>. <Box component="span" sx={{ opacity: 0.7 }}>Choose an action above to finish.</Box></>
                            )}
                        </Typography>
                    </Box>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <LoadingButton type="submit" loading={loading} variant="contained" size="small" sx={primaryBtnSx} startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}>
                        Create Rule
                    </LoadingButton>
                    <LoadingButton variant="outlined" size="small" sx={secondaryBtnSx}
                        onClick={() => reset({ ruleName: nextRuleName, initialAction: '', daysToReattempt: '', list: [{ fact: '', fieldValue: [] }] })}>
                        Reset
                    </LoadingButton>
                </Box>
            </FormProvider>
        </Box>
    );
};

export { NDRCreateAutomatedRule };
